import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import {
  getShortcutRange,
  type DateRange,
  type DateRangeShortcut,
} from "@/components/dashboard/DateRangeFilter";

export interface DashboardFilters {
  infringementType?: string;
  pirateService?: string;
}

export const FILTER_LABELS: Record<string, string> = {
  infringementType: "Type",
  pirateService: "Service",
};

interface DashboardFilterContextValue {
  filters: DashboardFilters;
  setFilter: (patch: Partial<DashboardFilters>) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  dateShortcut: DateRangeShortcut;
  dateRange: DateRange;
  customDateRange: DateRange;
  onShortcutChange: (shortcut: DateRangeShortcut, range: DateRange) => void;
  onCustomRangeChange: (range: DateRange) => void;
}

const DashboardFilterContext = createContext<DashboardFilterContextValue | null>(null);

export function DashboardFilterProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<DashboardFilters>({});
  const [dateShortcut, setDateShortcut] = useState<DateRangeShortcut>(() => {
    return (localStorage.getItem("soc:dashboard:dateShortcut") as DateRangeShortcut) ?? "3h";
  });
  const [dateRange, setDateRange] = useState<DateRange>(() => getShortcutRange(dateShortcut));
  const [customDateRange, setCustomDateRange] = useState<DateRange>({});

  const setFilter = useCallback((patch: Partial<DashboardFilters>) => {
    setFilters((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(patch) as [keyof DashboardFilters, unknown][]) {
        if (next[k] === v) {
          // Same value => toggle off
          delete next[k];
        } else {
          (next as Record<string, unknown>)[k] = v;
        }
      }
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => setFilters({}), []);
  const hasActiveFilters = Object.keys(filters).length > 0;

  const onShortcutChange = useCallback((shortcut: DateRangeShortcut, range: DateRange) => {
    localStorage.setItem("soc:dashboard:dateShortcut", shortcut);
    setDateShortcut(shortcut);
    setDateRange(range);
  }, []);

  const onCustomRangeChange = useCallback((range: DateRange) => {
    setCustomDateRange(range);
    setDateRange(range);
  }, []);

  return (
    <DashboardFilterContext.Provider
      value={{
        filters,
        setFilter,
        clearFilters,
        hasActiveFilters,
        dateShortcut,
        dateRange,
        customDateRange,
        onShortcutChange,
        onCustomRangeChange,
      }}
    >
      {children}
    </DashboardFilterContext.Provider>
  );
}

const SAFE_DEFAULT: DashboardFilterContextValue = {
  filters: {},
  setFilter: () => {},
  clearFilters: () => {},
  hasActiveFilters: false,
  dateShortcut: "3h",
  dateRange: getShortcutRange("3h"),
  customDateRange: {},
  onShortcutChange: () => {},
  onCustomRangeChange: () => {},
};

export function useDashboardFilter(): DashboardFilterContextValue {
  return useContext(DashboardFilterContext) ?? SAFE_DEFAULT;
}
